import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useMemo } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DateRangePicker from '~components/Filters/DateRangePicker';
import { OrderStateChip } from '~components/Order/OrderStatusChip';
import { Order } from '~hooks/useOrders/models';
import useDeletedRecordsCursor from '~pages/Settings/DeletedRecords/useDeletedRecordsCursor';
import useDeletedRecordsDateFilter from '~pages/Settings/DeletedRecords/useDeletedRecordsDateFilter';
import useDiscardedOrdersQuery from '~queries/useDiscardedOrdersQuery';

interface DeletedOrdersTabProps {
  companyId: string;
}

export default function DeletedOrdersTab({ companyId }: DeletedOrdersTabProps) {
  const { appliedRange, applyRange, draftRange, selectRange } =
    useDeletedRecordsDateFilter();

  // Whole-day boundaries must resolve in the company timezone or the window shifts a day.
  const startDateParam = appliedRange.startDate
    ? dayjs.tz(appliedRange.startDate.format('YYYY-MM-DD')).startOf('day').toISOString()
    : undefined;
  const endDateParam = appliedRange.endDate
    ? dayjs.tz(appliedRange.endDate.format('YYYY-MM-DD')).endOf('day').toISOString()
    : undefined;

  const filtersKey = JSON.stringify([companyId, startDateParam, endDateParam]);
  const { cursor, goToPage, paginationForDisplay } = useDeletedRecordsCursor(filtersKey);

  const { isFetching, orders, pagination } = useDiscardedOrdersQuery({
    companyId,
    cursor: cursor.cursor,
    direction: cursor.direction,
    endDate: endDateParam,
    startDate: startDateParam,
  });

  const columns = useMemo<Array<DataTableColumn<Order>>>(
    () => [
      {
        field: 'orderId',
        headerName: t('settings.deleted_records.columns.order_number'),
        valueGetter: (order) => order.orderId,
      },
      {
        field: 'project',
        headerName: t('form_fields.project'),
        valueGetter: (order) => order.projectName,
      },
      {
        field: 'customerAccount',
        headerName: t('common.customer'),
        valueGetter: (order) => order.account?.name,
      },
      {
        field: 'loadAt',
        headerName: t('settings.deleted_records.columns.load_date'),
        valueGetter: (order) =>
          order.loadAt ? dayjs.tz(order.loadAt).format('MM/DD/YYYY') : '',
      },
      {
        field: 'state',
        headerName: t('form_fields.status'),
        renderCell: (order) => <OrderStateChip hideDropdownArrow state={order.state} />,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        {/* Labelled for orders.load_at, the scheduled date — not the deletion date. */}
        <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
          {t('settings.deleted_records.filters.load_date')}
        </Typography>
        <DateRangePicker
          allowAllDates
          showShortcuts
          endDate={draftRange.endDate}
          startDate={draftRange.startDate}
          onAnchorClose={applyRange}
          onDateSelect={selectRange}
        />
      </Box>

      <DataTable
        id="deleted-records-orders-grid"
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            pagination={paginationForDisplay(pagination)}
            onNavigate={(direction) => goToPage(direction, pagination)}
          />
        }
        loading={isFetching}
        noRowsOverlayText={t('settings.deleted_records.no_deleted_orders')}
        rows={orders}
      />
    </Box>
  );
}
